/* eslint-disable no-underscore-dangle */
/**
 * WHERE A VEHICLE'S COSTS ARE CHARGED — the screen's copy of the API's
 * `Helpers/expenseCostOperator.helper.ts` and `Schemas/Zod/Cars.schema.ts`.
 *
 * A driver files a parking receipt against a CAR. The API turns that into a
 * ledger line by reading the car's own `cost_operators` map:
 *
 *     Car.cost_operators = { parking: '<operator id>', fuel: …, rental: …, other: … }
 *
 * A type with no entry is not an error. It is the normal state of every
 * third-party vehicle, and the approve screen then defaults that cost to the
 * operator on the trip. It was ABSORBED until 2026-11-02; see
 * `Utils/expenseCostDestination`. Nothing in the dashboard could set
 * this map until now; the two vehicles that have one were written by hand.
 *
 * TWO THINGS HERE ARE LOAD-BEARING, and neither is obvious from the screen.
 *
 * 1. THE WRITE REPLACES THE WHOLE MAP. `CarsController.costOperatorsToStore`
 *    rebuilds the stored object from all four `EXPENSE_COST_TYPES` of the
 *    body it was given, so a request carrying `{ parking: … }` alone does
 *    not merge — it stores `{ parking: … }` and DROPS fuel and rental. Any
 *    caller that sends the field at all must send the complete map, which is
 *    what `costOperatorsPayload` is for, and is the reason the screen offers
 *    all four types rather than the two the driver app can file today: a
 *    field the screen cannot show is a field it cannot safely resend.
 *
 * 2. ABSENT IS NOT EMPTY. The same controller treats the field being absent
 *    from the body as "leave whatever is on the row alone", and only an
 *    explicitly present map clears anything. So an ordinary vehicle edit
 *    must OMIT `cost_operators` entirely, and clearing must SEND it with the
 *    emptied key blank. `costOperatorsPayload` builds the explicit form;
 *    deciding whether to send it at all belongs to the caller.
 */

/**
 * Every cost type the map can hold. Must match the API's
 * `EXPENSE_COST_TYPES` exactly — its schema is `.strict()`, so an extra key
 * is a 400 and a missing one is a silent clear.
 */
export const EXPENSE_COST_TYPES = ['parking', 'fuel', 'rental', 'other'];

/**
 * The types a driver can actually file from the app today — the API's
 * `DRIVER_EXPENSE_TYPES`. `fuel` and `rental` are shown anyway (see 1 above,
 * and `COST_TYPE_NOTES`) but are labelled so nobody thinks routing one of
 * them does something this week.
 */
export const FILEABLE_COST_TYPES = ['parking', 'other'];

export const COST_TYPE_LABELS = {
  parking: 'Parking',
  fuel: 'Fuel',
  rental: 'Rental',
  other: 'Other',
};

/** Why a type is on screen when nothing can file it yet. */
export const NOT_YET_FILEABLE = 'no driver receipts of this type yet';

export const isFileableCostType = (costType) => FILEABLE_COST_TYPES.includes(costType);

/** The note under a type, or '' for the two that are live. */
export const costTypeNote = (costType) => (isFileableCostType(costType) ? '' : NOT_YET_FILEABLE);

/**
 * A car's map, from whatever the row hands back: known keys only, non-blank
 * strings only, trimmed. Mirrors the API's `costOperatorsOf`, so what the
 * screen shows is what the API would derive from the same row.
 */
export const costOperatorsOf = (car) => {
  const raw = car?.cost_operators;
  if (!raw || typeof raw !== 'object') return {};

  const out = {};

  EXPENSE_COST_TYPES.forEach((costType) => {
    const value = raw[costType];
    if (typeof value === 'string' && value.trim()) out[costType] = value.trim();
  });

  return out;
};

/** The same map with one type set, or — for a blank choice — cleared. */
export const withCostOperator = (car, costType, operatorId) => {
  const next = costOperatorsOf(car);

  if (typeof operatorId === 'string' && operatorId.trim()) {
    next[costType] = operatorId.trim();
  } else {
    delete next[costType];
  }

  return next;
};

/**
 * THE MAP AS IT IS SENT: every type present, a cleared one as ''.
 *
 * Complete on purpose. The controller rebuilds the stored object from the
 * body, so a partial map silently blanks the types it left out — and a
 * blank string is how a type is cleared, since the API's `costOperatorId`
 * transforms '' to undefined and then omits it from what it stores.
 *
 * Sending this at all means "this is the vehicle's whole mapping now".
 * Omitting the field means "do not touch it"; that choice is the caller's.
 */
export const costOperatorsPayload = (car) => {
  const current = costOperatorsOf(car);

  return EXPENSE_COST_TYPES.reduce((payload, costType) => (
    { ...payload, [costType]: current[costType] || '' }
  ), {});
};

/**
 * What the vehicle's row shows when nothing is being edited: one entry per
 * type that is actually routed, named rather than id'd where the operator is
 * in hand. An unknown id still lists — the link is the fact, the name is a
 * label, and a mapping pointing at a record we cannot name is exactly the
 * thing somebody needs to see.
 */
export const describeCostOperators = (car, operatorsById = {}) => {
  const current = costOperatorsOf(car);

  return EXPENSE_COST_TYPES
    .filter((costType) => current[costType])
    .map((costType) => ({
      costType,
      label: COST_TYPE_LABELS[costType],
      operatorId: current[costType],
      name: operatorsById[current[costType]]?.name || current[costType],
    }));
};

/** id -> operator, for naming a mapping without scanning the list per row. */
export const byId = (operators) => (operators || []).reduce((map, operator) => (
  operator?.Id ? { ...map, [operator.Id]: operator } : map
), {});

export default {
  EXPENSE_COST_TYPES,
  FILEABLE_COST_TYPES,
  COST_TYPE_LABELS,
  NOT_YET_FILEABLE,
  isFileableCostType,
  costTypeNote,
  costOperatorsOf,
  withCostOperator,
  costOperatorsPayload,
  describeCostOperators,
  byId,
};
